import * as React from 'react';
import { css, useTheme } from '@emotion/react';
import { CloseAlt, IconButton, Inline, Stack, Text, useToast, usePrevious } from '@resi-media/resi-ui';
import { differenceInSeconds as diffInSeconds, parseISO } from 'date-fns';
import { generatePath, useLocation, useNavigate } from 'react-router';
import { createContainer } from 'react-tracked';
import useSWR from 'swr';
import type { UnionOf } from 'ts-toolbelt/out/Object/UnionOf';
import { LiveJoinNowButton } from '@sites/components/LiveJoinNowButton';
import { GOOGLE_EVENTS } from '@sites/constants/google-events';
import {
  HALF_HOUR,
  LIVE_PLAYER_CHANNELS_POLL_INTERVAL,
  LIVE_VIDEO_STATUS,
  FORTY_EIGHT_HOURS,
} from '@sites/constants/live-player';
import type CustomError from '@sites/helpers/customError';
import { useElementVisibilityById } from '@sites/hooks/useElementVisibilityById';
import * as globalStyles from '@sites/styles/global';
import type { Get } from '@sites/types/channels/get';
import { usePrefix } from '@studio/hooks/use-prefix';
import { useEnvironment } from '../environmentContext';
import { useFeed } from '../feedContext';

type LiveEvent = Get.NextLiveEvent | null;

type LiveVideoStatus = UnionOf<typeof LIVE_VIDEO_STATUS>;

type LivePlayerContextHook = {
  channelName: string;
  countdownSeconds: number;
  error?: CustomError;
  isLive: boolean;
  isLoading: boolean;
  isNotFound: boolean;
  liveVideoStatus: LiveVideoStatus;
  nextLiveEvent: LiveEvent;
  setLiveVideoStatus: React.Dispatch<React.SetStateAction<LiveVideoStatus>>;
  showBanner: boolean;
  showJoinEarly: boolean;
  showLiveCountdown: boolean;
  showLiveVideo: boolean;
  videoDescription: string;
  videoName: string;
  webChannelId: string;
};

const useValues = ({
  pollInterval = LIVE_PLAYER_CHANNELS_POLL_INTERVAL,
}: {
  pollInterval?: number;
}): readonly [LivePlayerContextHook, () => void] => {
  const customerSite = window.location.pathname.split('/')[1];
  const { prefixNS } = usePrefix('components:', 'livePlayerToast');
  const { pathname } = useLocation();
  const previousPathname = usePrevious(pathname);
  const { SITES_API_V1, sitesClientUrl } = useEnvironment();
  const { site, tokenData } = useFeed();
  const { remove, trigger } = useToast();
  const theme = useTheme();
  const isBannerVisible = useElementVisibilityById('live-player-banner-id');
  const isHeroVisible = useElementVisibilityById('hero-section-id');
  const isVideoPageVisible = useElementVisibilityById('live-video-id');
  const navigate = useNavigate();

  const channelUrl = `${sitesClientUrl}${customerSite}`;

  const apiUrl = React.useMemo(() => `${SITES_API_V1}/channels?url=${channelUrl}`, [channelUrl, SITES_API_V1]);

  const {
    data,
    error,
    isLoading: isSwrLoading,
    mutate,
  } = useSWR<Get.LivePlayerApiResponse, CustomError>(
    tokenData?.jwt && site?.channelId ? [apiUrl, tokenData.jwt] : null,
    null,
    {
      refreshInterval: pollInterval,
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  const isLoading = isSwrLoading || !tokenData?.jwt || !site?.channelId;
  const webChannelId = data?.channelId ?? '';

  const [countdownSeconds, setCountdownSeconds] = React.useState(
    data?.nextLive?.startTime ? diffInSeconds(parseISO(data.nextLive.startTime), new Date()) : 0
  );
  const prevCountdownSeconds = React.useRef(countdownSeconds);
  const hasTriggeredToast = React.useRef(false);
  const [liveVideoStatus, setLiveVideoStatus] = React.useState<LiveVideoStatus>(LIVE_VIDEO_STATUS.NEVER_STARTED);
  const [previousLiveEvent, setPreviousLiveEvent] = React.useState<LiveEvent>(null);
  const [hasInitialLoadCompleted, setHasInitialLoadCompleted] = React.useState(false);
  const isLive = countdownSeconds <= 0 && Boolean(data?.live);
  const showLiveCountdown = countdownSeconds > 0 && countdownSeconds <= FORTY_EIGHT_HOURS;
  const showJoinEarly = countdownSeconds > 0 && countdownSeconds <= HALF_HOUR;
  const showLiveVideo =
    isLive ||
    liveVideoStatus === LIVE_VIDEO_STATUS.STARTED ||
    (countdownSeconds <= 0 && liveVideoStatus === LIVE_VIDEO_STATUS.ENDED);
  const showBanner = showLiveCountdown || showLiveVideo;
  const isNotFound = !isLoading && (liveVideoStatus === LIVE_VIDEO_STATUS.ENDED || !isLive);
  const nextLiveEvent = data?.nextLive ?? null;
  const videoDescription = nextLiveEvent?.description ?? previousLiveEvent?.description ?? '';
  const videoName = nextLiveEvent?.name ?? previousLiveEvent?.name ?? '';

  React.useEffect(() => {
    if (previousPathname && previousPathname !== pathname) {
      setHasInitialLoadCompleted(false);
      setLiveVideoStatus(LIVE_VIDEO_STATUS.NEVER_STARTED);
      hasTriggeredToast.current = false;
      remove(customerSite);
    }

    const timer = setTimeout(() => {
      setHasInitialLoadCompleted(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, [customerSite, pathname, previousPathname, remove]);

  React.useEffect(() => {
    const timer = setInterval(() => {
      const startTime = data?.nextLive?.startTime;
      if (!startTime) {
        setCountdownSeconds(0);
        return;
      }
      const next = diffInSeconds(parseISO(startTime), new Date());
      setCountdownSeconds(next > 0 ? next : 0);
    }, 1000);

    return () => clearInterval(timer);
  }, [data?.nextLive?.startTime]);

  React.useEffect(() => {
    if (
      isLive &&
      hasInitialLoadCompleted &&
      !hasTriggeredToast.current &&
      !isBannerVisible &&
      !isHeroVisible &&
      !isVideoPageVisible
    ) {
      hasTriggeredToast.current = true;
      trigger({
        shouldCloseOnTimeout: false,
        id: customerSite,
        isPositionTop: false,
        children: (
          <Stack alignItems="flex-start" id="live-video-toast-container">
            <Inline alignItems="flex-start" justifyContent="space-between" width="scale">
              <Stack gap="xs">
                <Text variant="body3">{prefixNS('liveBroadcastHasStarted')}</Text>
                <Text css={(theme) => globalStyles.truncateText(theme)} variant="h5" weightVariant="bold">
                  {data?.nextLive?.name}
                </Text>
              </Stack>
              <IconButton colorVariant="neutral" label="Close" onClick={() => remove(customerSite)}>
                <CloseAlt
                  css={(theme) => css`
                    color: ${theme.palette.contrast};
                    font-size: ${theme.typography.pxToRem(14)};
                  `}
                />
              </IconButton>
            </Inline>
            <LiveJoinNowButton
              onClick={() => {
                navigate(
                  generatePath('/:customerSite/live/:webChannelId', {
                    customerSite,
                    webChannelId,
                  })
                );
                gtag('event', GOOGLE_EVENTS.LIVE_STREAM_CLICK, { live_cta: 'toast' });
              }}
              showJoinEarly={showJoinEarly}
            />
          </Stack>
        ),
        customCss: {
          container: () => ({
            backgroundColor: theme.palette.surface.canvas,
            borderRadius: theme.shape.borderRadius.m,
          }),
        },
      });
    } else if (liveVideoStatus === LIVE_VIDEO_STATUS.ENDED) {
      hasTriggeredToast.current = false;
    } else if ((isBannerVisible || isHeroVisible || isVideoPageVisible) && hasTriggeredToast.current) {
      hasTriggeredToast.current = false;
      remove(customerSite);
    }
  }, [
    customerSite,
    data?.nextLive?.name,
    isBannerVisible,
    isVideoPageVisible,
    isHeroVisible,
    isLive,
    hasInitialLoadCompleted,
    liveVideoStatus,
    navigate,
    prefixNS,
    trigger,
    theme.palette.surface.canvas,
    theme.shape.borderRadius.m,
    remove,
    showJoinEarly,
    webChannelId,
  ]);

  React.useEffect(() => {
    if (isLive && data?.nextLive) {
      setPreviousLiveEvent(data.nextLive);
    }
  }, [data?.nextLive, isLive]);

  React.useEffect(() => {
    if (prevCountdownSeconds.current > 0 && countdownSeconds <= 0) {
      mutate();
    }
    prevCountdownSeconds.current = countdownSeconds;
  }, [countdownSeconds, mutate]);

  const state = React.useMemo(
    () => ({
      channelName: data?.channelName ?? '',
      countdownSeconds,
      error,
      isLive,
      isLoading,
      isNotFound,
      liveVideoStatus,
      nextLiveEvent,
      setLiveVideoStatus,
      showBanner,
      showJoinEarly,
      showLiveCountdown,
      showLiveVideo,
      videoDescription,
      videoName,
      webChannelId,
    }),
    [
      countdownSeconds,
      data?.channelName,
      error,
      isLive,
      isLoading,
      isNotFound,
      liveVideoStatus,
      nextLiveEvent,
      setLiveVideoStatus,
      showBanner,
      showJoinEarly,
      showLiveCountdown,
      showLiveVideo,
      videoDescription,
      videoName,
      webChannelId,
    ]
  );

  return [
    state,
    () => {
      throw new Error('use functions in the state');
    },
  ];
};

const {
  Provider: LivePlayerProvider,
  useTrackedState: useLivePlayer,
  useUpdate: useLivePlayerUpdate,
} = createContainer<LivePlayerContextHook, () => void, { children: React.ReactNode; pollInterval?: number }>(useValues);

export { LivePlayerProvider, useLivePlayer, useLivePlayerUpdate };
export type { LivePlayerContextHook };
